import { theme, type ThemeConfig } from 'antd';

/**
 * AntD ConfigProvider theme derived from the D-Table brand tokens (CLAUDE.md §2.1).
 * Keep these values in lockstep with tokens.css — same hex codes, one source of truth.
 */
export const brand = {
  primary: '#4338CA',
  primaryDark: '#312E81',
  primaryLight: '#6366F1',
  accent: '#F59E0B',
  bg: '#F5F6FB',
  surface: '#FFFFFF',
  border: '#E4E4EF',
  textPrimary: '#1E1B2E',
  textSecondary: '#52526B',
  textMuted: '#9899B0',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  info: '#2563EB',
  sidebarBg: '#1E1B4B',
  sidebarActive: '#4338CA',
  sidebarText: '#C7C9E5',
} as const;

/**
 * Dark-mode counterpart. Must stay in lockstep with the
 * `:root[data-theme='dark']` block in tokens.css.
 */
export const brandDark = {
  primary: '#818CF8',
  primaryDark: '#6366F1',
  primaryLight: '#A5B4FC',
  accent: '#FBBF24',
  bg: '#0B0D17',
  surface: '#12141F',
  border: '#262837',
  textPrimary: '#E5E7F0',
  textSecondary: '#A4A7B8',
  textMuted: '#6B7086',
  success: '#34D399',
  warning: '#FBBF24',
  danger: '#F87171',
  info: '#60A5FA',
  sidebarBg: '#060811',
  sidebarActive: '#6366F1',
  sidebarText: '#B4B7CB',
} as const;

export type ThemeMode = 'light' | 'dark';

/**
 * Returns the AntD ThemeConfig for the requested mode. Uses AntD's built-in
 * `darkAlgorithm` under the hood so every component (Modal, Dropdown, Popover
 * portals) picks up dark styling automatically without a per-component patch.
 */
export function buildAntdTheme(mode: ThemeMode = 'light'): ThemeConfig {
  const t = mode === 'dark' ? brandDark : brand;
  return {
    cssVar: true,
    hashed: false,
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: t.primary,
      colorInfo: t.info,
      colorSuccess: t.success,
      colorWarning: t.warning,
      colorError: t.danger,
      colorBgBase: t.surface,
      colorBgLayout: t.bg,
      colorText: t.textPrimary,
      colorTextSecondary: t.textSecondary,
      colorTextTertiary: t.textMuted,
      colorBorder: t.border,
      borderRadius: 6,
      fontFamily:
        "'Inter', 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: 16,
      wireframe: false,
    },
    components: {
      Layout: {
        headerBg: t.surface,
        headerHeight: 56,
        headerPadding: '0 16px',
        siderBg: t.sidebarBg,
        bodyBg: t.bg,
      },
      Menu: {
        darkItemBg: t.sidebarBg,
        darkItemColor: t.sidebarText,
        darkItemSelectedBg: t.sidebarActive,
        darkItemSelectedColor: '#FFFFFF',
        darkItemHoverBg: 'rgba(255,255,255,0.06)',
      },
      Table: {
        headerBg: mode === 'dark' ? '#1A1D2B' : '#F0F3F8',
        headerColor: t.textSecondary,
        rowHoverBg: mode === 'dark' ? '#1A1D2B' : '#F7F9FC',
        cellPaddingBlock: 10,
        cellPaddingInline: 12,
      },
      Card: {
        boxShadowTertiary:
          mode === 'dark'
            ? '0 1px 2px 0 rgba(0, 0, 0, 0.5)'
            : '0 1px 2px 0 rgba(30, 27, 75, 0.06)',
      },
      Button: { controlHeight: 34 },
      Input: { controlHeight: 34 },
      Select: { controlHeight: 34 },
      DatePicker: { controlHeight: 34 },
    },
  };
}

/** Backwards-compatible default (light). Existing imports keep working. */
export const antdTheme: ThemeConfig = buildAntdTheme('light');
